// MARK://dhk8/pos/hooks/useBridgeStatus
// Hook reactivo para el estado del NintaiGO Bridge — polling cada 10s.
//
// Pensado para que múltiples componentes (BridgeDetector, PrintingSettingsPage
// secciones 2-5, futuros indicadores en el shell) compartan UN solo poller —
// el hook usa una suscripción global con refcount.
//
// Estados expuestos:
//   - status: BridgeStatus | null  (null = primer probe en curso)
//   - lastCheckedAt: ISO string
//   - refresh(): fuerza un probe inmediato

import { useEffect, useState } from 'react'
import { getStatus, type BridgeStatus } from '../lib/bridge/bridge-client.js'

const POLL_INTERVAL_MS = 10_000

type Listener = (next: { status: BridgeStatus | null; lastCheckedAt: string | null }) => void

let currentStatus: BridgeStatus | null = null
let lastCheckedAt: string | null = null
let inFlight: Promise<void> | null = null
const listeners = new Set<Listener>()
let pollTimer: ReturnType<typeof setInterval> | null = null

function notify(): void {
  for (const fn of listeners) fn({ status: currentStatus, lastCheckedAt })
}

async function probe(): Promise<void> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      currentStatus = await getStatus()
    } catch (e) {
      currentStatus = { state: 'offline', reason: e instanceof Error ? e.message : 'unknown' }
    } finally {
      lastCheckedAt = new Date().toISOString()
      inFlight = null
      notify()
    }
  })()
  return inFlight
}

function ensurePolling(): void {
  if (pollTimer !== null) return
  pollTimer = setInterval(() => { void probe() }, POLL_INTERVAL_MS)
}

function stopPolling(): void {
  if (pollTimer === null) return
  clearInterval(pollTimer)
  pollTimer = null
}

export interface UseBridgeStatusReturn {
  status: BridgeStatus | null
  lastCheckedAt: string | null
  refresh: () => Promise<void>
}

export function useBridgeStatus(): UseBridgeStatusReturn {
  const [snap, setSnap] = useState<{ status: BridgeStatus | null; lastCheckedAt: string | null }>(
    { status: currentStatus, lastCheckedAt }
  )

  useEffect(() => {
    const fn: Listener = (next) => setSnap(next)
    listeners.add(fn)
    // Primer probe inmediato si aún no hay nada cacheado o cache > 10s.
    const isStale = !lastCheckedAt || (Date.now() - new Date(lastCheckedAt).getTime() > POLL_INTERVAL_MS)
    if (isStale) void probe()
    else fn({ status: currentStatus, lastCheckedAt })
    ensurePolling()
    return () => {
      listeners.delete(fn)
      if (listeners.size === 0) stopPolling()
    }
  }, [])

  return {
    status: snap.status,
    lastCheckedAt: snap.lastCheckedAt,
    refresh: probe,
  }
}

/** Snapshot no-reactivo del último probe — útil desde callers no-React
 *  (stores, callbacks one-shot) que quieren tomar decisiones rápidas sin
 *  esperar al ciclo del polling. NO dispara probe nuevo. */
export function getCachedBridgeStatus(): BridgeStatus | null {
  return currentStatus
}
